import React, { useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useGSAP } from '@gsap/react';

const Hero: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLHeadingElement>(null);
  const subRef = useRef<HTMLParagraphElement>(null);
  const founderRef = useRef<HTMLParagraphElement>(null);
  const imgRef = useRef<HTMLDivElement>(null);
  const btnRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useGSAP(() => {
    const tl = gsap.timeline({ defaults: { ease: 'power3.out' } });

    // Initial Setup to prevent FOUC (Flash of Unstyled Content)
    gsap.set([textRef.current, subRef.current, founderRef.current, btnRef.current], { autoAlpha: 0 });

    tl.fromTo(imgRef.current,
      { scale: 1.2 },
      { scale: 1, duration: 2.5, ease: 'power2.out' }
    )
      .fromTo(textRef.current,
        { y: 100, autoAlpha: 0 },
        { y: 0, autoAlpha: 1, duration: 1.5 },
        '-=2'
      )
      .fromTo(subRef.current,
        { y: 30, autoAlpha: 0 },
        { y: 0, autoAlpha: 1, duration: 1 },
        '-=1.2'
      )
      .fromTo(founderRef.current,
        { scaleX: 0, autoAlpha: 0 },
        { scaleX: 1, autoAlpha: 1, duration: 1.2, ease: 'expo.out' },
        '-=0.8'
      )
      .fromTo(btnRef.current,
        { y: 30, autoAlpha: 0 },
        { y: 0, autoAlpha: 1, duration: 1, clearProps: 'transform' }, // Only clear transform so opacity/visibility stays
        '-=0.5'
      );

    // Parallax Effect
    gsap.to(textRef.current, {
      yPercent: -50,
      ease: 'none',
      scrollTrigger: {
        trigger: containerRef.current,
        start: 'top top',
        end: 'bottom top',
        scrub: true,
      },
    });
  }, { scope: containerRef });

  return (
    <section ref={containerRef} className="relative min-h-screen w-full overflow-hidden flex items-center justify-center bg-diwan-900">
      {/* Background Image Layer */}
      <div className="absolute inset-0 z-0">
        <div
          ref={imgRef}
          className="w-full h-full bg-cover bg-center"
          style={{
            backgroundImage: 'url("https://images.unsplash.com/photo-1618221195710-dd6b41faaea6?q=80&w=2500&auto=format&fit=crop")'
          }}
        />
        {/* Lux Overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-white/10 via-transparent to-black/20 mix-blend-multiply"></div>
      </div>

      <div className="relative z-20 text-center px-4 md:px-6 max-w-7xl mx-auto flex flex-col items-center justify-center h-full pt-20">
        <p ref={subRef} className="font-sans text-diwan-gold/90 tracking-[0.4em] text-xs md:text-sm uppercase mb-6 font-semibold drop-shadow-sm opacity-0">
          Excellence Since 1958
        </p>

        <h1 ref={textRef} className="font-serif text-5xl md:text-8xl lg:text-9xl text-[#FF5E5E] font-medium leading-[0.9] drop-shadow-lg opacity-0">
          D'MANS <br />
          <span className="italic text-diwan-gold text-5xl md:text-7xl lg:text-8xl block mt-2 md:mt-4">
            Classic & Modern
          </span>
        </h1>

        <div className="mt-8 md:mt-12 overflow-hidden">
          <p ref={founderRef} className="font-sans text-diwan-cream text-xs md:text-sm tracking-[0.2em] uppercase border-t border-diwan-cream/30 pt-6 px-8 drop-shadow-sm font-semibold opacity-0">
            Ranbir Singh Dhiman & Sons
          </p>
        </div>

        {/* CTA Buttons */}
        <div
          ref={btnRef}
          className="mt-12 md:mt-16 pb-12 flex flex-col md:flex-row gap-6 justify-center items-center"
        >
          <button
            onClick={() => navigate('/products')}
            className="group relative flex items-center gap-4 px-10 py-4 bg-white/20 backdrop-blur-md border border-white/40 hover:bg-diwan-gold hover:border-diwan-gold transition-all duration-500 rounded-sm shadow-xl hover:shadow-2xl hover:-translate-y-1 overflow-hidden min-w-[200px] justify-center"
          >
            {/* Shiny overlay effect on hover */}
            <div className="absolute inset-0 -translate-x-full group-hover:animate-[shimmer_1.5s_infinite] bg-gradient-to-r from-transparent via-white/20 to-transparent z-0"></div>

            <span className="relative z-10 text-diwan-cream group-hover:text-white uppercase tracking-[0.2em] text-xs font-bold transition-colors duration-300">
              Explore Our Work
            </span>
            <ArrowRight size={18} className="relative z-10 text-diwan-cream group-hover:text-white transition-colors duration-300 group-hover:translate-x-1" />
          </button>

          <button
            onClick={() => navigate('/contact')}
            className="group relative flex items-center gap-4 px-10 py-4 bg-transparent border border-white/20 hover:bg-white hover:border-white transition-all duration-500 rounded-sm shadow-lg hover:shadow-xl hover:-translate-y-1 overflow-hidden min-w-[200px] justify-center"
          >
            <span className="relative z-10 text-diwan-cream group-hover:text-diwan-900 uppercase tracking-[0.2em] text-xs font-bold transition-colors duration-300">
              Contact Us
            </span>
          </button>
        </div>
      </div>

      <div className="absolute bottom-8 right-8 hidden md:block z-20 mix-blend-difference text-white">
        <p className="text-right font-sans text-[10px] tracking-[0.3em] uppercase opacity-60">
          Scroll to Explore
        </p>
      </div>
    </section>
  );
};

export default Hero;